import gql from 'graphql-tag'
import {
    RULE_FIELDS,
    RULE_INTERVAL_FIELDS,
    GROUP_CARD_FIELDS,
    GROUP_COMPANY_FIELDS,
    GROUP_FIELDS,
    GROUP_READER_RULE_FIELDS,
} from './fragments'

export const CREATE_ACCESS_GROUP_COMPANY_ASSOCIATION = gql`
    mutation createAccessGroupCompanyAssociation($groupId: ID!, $companyId: ID) {
        createAccessGroupCompanyAssociation(input: { groupId: $groupId, companyId: $companyId }) {
            association {
                ...GroupCompanyFields
            }
            errors {
                ... on NotAllowedError {
                    correlationId
                    message
                }
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on BklrProjectCompletedError {
                    correlationId
                    message
                }
                ... on CompanyNotFoundError {
                    correlationId
                    message
                }
                ... on AccessGroupNotFoundError {
                    correlationId
                    message
                }
            }
        }
    }
    ${GROUP_COMPANY_FIELDS}
`

export const CREATE_ACCESS_GROUP_CARD_ASSOCIATION = gql`
    mutation createAccessGroupCardAssociation($cardId: CardId!, $groupId: ID!, $withPsi: Boolean!) {
        createAccessGroupCardAssociation(input: { cardId: $cardId, groupId: $groupId }) {
            association {
                ...GroupCardFields
            }
            errors {
                ... on AccessGroupNotFoundError {
                    correlationId
                    message
                }
                ... on BklrProjectCompletedError {
                    correlationId
                    message
                }
                ... on CardInvalidError {
                    correlationId
                    message
                }
                ... on CardNotFoundError {
                    correlationId
                    isVirtual
                    message
                }
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
            }
        }
    }
    ${GROUP_CARD_FIELDS}
`

export const DELETE_ACCESS_GROUP_COMPANY_ASSOCIATION = gql`
    mutation deleteAccessGroupCompanyAssociation($groupId: ID!, $companyId: ID) {
        deleteAccessGroupCompanyAssociation(input: { companyId: $companyId, groupId: $groupId }) {
            errors {
                ... on AccessGroupNotFoundError {
                    correlationId
                    message
                }
                ... on BklrProjectCompletedError {
                    correlationId
                    message
                }
                ... on CompanyNotFoundError {
                    correlationId
                    message
                }
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
            }
        }
    }
`

export const DELETE_ACCESS_GROUP_CARD_ASSOCIATION = gql`
    mutation deleteAccessGroupCardAssociation($cardId: CardId!, $groupId: ID!) {
        deleteAccessGroupCardAssociation(input: { cardId: $cardId, groupId: $groupId }) {
            errors {
                ... on AccessGroupNotFoundError {
                    correlationId
                    message
                }
                ... on BklrProjectCompletedError {
                    correlationId
                    message
                }
                ... on CardInvalidError {
                    correlationId
                    message
                }
                ... on CardNotFoundError {
                    correlationId
                    isVirtual
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
            }
        }
    }
`

export const CREATE_ACCESS_RULE = gql`
    mutation createAccessRule($projectId: ID!, $name: String!) {
        createAccessRule(input: { projectId: $projectId, name: $name }) {
            rule {
                ...RuleFields
            }
            errors {
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
                ... on ProjectNotFoundError {
                    correlationId
                    message
                }
            }
            rule {
                ...RuleFields
            }
        }
    }
    ${RULE_FIELDS}
`

export const DELETE_ACCESS_RULE = gql`
    mutation deleteAccessRule($ruleId: ID!) {
        deleteAccessRule(input: { ruleId: $ruleId }) {
            errors {
                ... on NotAllowedError {
                    correlationId
                    message
                }
                ... on MutationTargetNotFoundError {
                    correlationId
                    message
                }
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
            }
        }
    }
`

export const CREATE_ACCESS_RULE_INTERVAL_FOR_EXISTING_RULE = gql`
    mutation createAccessInterval(
        $ruleId: ID!
        $days: [WeekDay!]!
        $from: ClockTimeWith2400!
        $to: ClockTimeWith2400!
        $requiresPin: Boolean!
    ) {
        createAccessInterval(
            input: { days: $days, from: $from, requiresPin: $requiresPin, ruleId: $ruleId, to: $to }
        ) {
            errors {
                ... on MutationTargetNotFoundError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
            }
            interval {
                ...RuleIntervalFields
            }
            query {
                accessRule(ruleId: $ruleId) {
                    ...RuleFields
                }
            }
        }
    }
    ${RULE_FIELDS}
    ${RULE_INTERVAL_FIELDS}
`

export const CREATE_ACCESS_RULE_INTERVAL = gql`
    mutation createAccessInterval(
        $projectId: ID!
        $ruleId: ID!
        $days: [WeekDay!]!
        $from: ClockTimeWith2400!
        $to: ClockTimeWith2400!
        $requiresPin: Boolean!
    ) {
        createAccessInterval(
            input: { days: $days, from: $from, requiresPin: $requiresPin, ruleId: $ruleId, to: $to }
        ) {
            interval {
                ...RuleIntervalFields
            }
            errors {
                ... on MutationTargetNotFoundError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
            }
            query {
                allRulesForProject(projectId: $projectId) {
                    ...RuleFields
                }
            }
        }
    }
    ${RULE_INTERVAL_FIELDS}
    ${RULE_FIELDS}
`

export const SET_ACCESS_RULE_NAME = gql`
    mutation setAccessRuleName($ruleId: ID!, $name: String!) {
        setAccessRuleName(input: { ruleId: $ruleId, name: $name }) {
            errors {
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on MutationTargetNotFoundError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
            }
            rule {
                ...RuleFields
            }
        }
    }
    ${RULE_FIELDS}
`

export const UPDATE_PROJECT_SETTING_DENY_INVALID_CARDS = gql`
    mutation setProjectSettingsDenyInvalidCards($denyInvalidCards: Boolean!, $projectId: ID!) {
        setProjectSettingsDenyInvalidCards(
            input: { denyInvalidCards: $denyInvalidCards, projectId: $projectId }
        ) {
            errors {
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
                ... on ProjectNotFoundError {
                    correlationId
                    message
                }
            }
            settings {
                denyInvalidCards
                denyWithoutPsi
            }
        }
    }
`

export const UPDATE_PROJECT_SETTING_DENY_WITHOUT_PSI = gql`
    mutation setProjectSettingsDenyWithoutPsi($denyWithoutPsi: Boolean!, $projectId: ID!) {
        setProjectSettingsDenyWithoutPsi(
            input: { denyWithoutPsi: $denyWithoutPsi, projectId: $projectId }
        ) {
            errors {
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
                ... on ProjectNotFoundError {
                    correlationId
                    message
                }
            }
            settings {
                denyInvalidCards
                denyWithoutPsi
            }
        }
    }
`

export const SET_ACCESS_RULE_INTERVAL_REQUIRES_PIN = gql`
    mutation setAccessIntervalRequiresPin($ruleId: ID!, $intervalId: ID!, $requiresPin: Boolean!) {
        setAccessIntervalRequiresPin(
            input: { intervalId: $intervalId, requiresPin: $requiresPin }
        ) {
            errors {
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on MutationTargetNotFoundError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
            }
            query {
                accessRule(ruleId: $ruleId) {
                    ...RuleFields
                }
            }
        }
    }
    ${RULE_FIELDS}
`

export const DELETE_ACCESS_RULE_INTERVAL = gql`
    mutation deleteAccessInterval($intervalId: ID!) {
        deleteAccessInterval(input: { intervalId: $intervalId }) {
            errors {
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on MutationTargetNotFoundError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
            }
            rule {
                ...RuleFields
            }
        }
    }
    ${RULE_FIELDS}
`

export const SET_ACCESS_RULE_INTERVAL_DAYS = gql`
    mutation setAccessIntervalDays($days: [WeekDay!]!, $intervalId: ID!, $ruleId: ID!) {
        setAccessIntervalDays(input: { days: $days, intervalId: $intervalId }) {
            errors {
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on MutationTargetNotFoundError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
            }
            query {
                accessRule(ruleId: $ruleId) {
                    ...RuleFields
                }
            }
        }
    }
    ${RULE_FIELDS}
`

export const SET_ACCESS_RULE_INTERVAL_FROM = gql`
    mutation setAccessIntervalFrom($intervalId: ID!, $from: ClockTimeWith2400!, $ruleId: ID!) {
        setAccessIntervalFrom(input: { intervalId: $intervalId, from: $from }) {
            errors {
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on MutationTargetNotFoundError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
            }
            query {
                accessRule(ruleId: $ruleId) {
                    ...RuleFields
                }
            }
        }
    }
    ${RULE_FIELDS}
`

export const SET_ACCESS_RULE_INTERVAL_TO = gql`
    mutation setAccessIntervalTo($intervalId: ID!, $to: ClockTimeWith2400!, $ruleId: ID!) {
        setAccessIntervalTo(input: { intervalId: $intervalId, to: $to }) {
            errors {
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on MutationTargetNotFoundError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
            }
            query {
                accessRule(ruleId: $ruleId) {
                    ...RuleFields
                }
            }
        }
    }
    ${RULE_FIELDS}
`

export const CREATE_ACCESS_GROUP = gql`
    mutation createAccessGroup($projectId: ID!, $name: String!) {
        createAccessGroup(input: { name: $name, projectId: $projectId }) {
            errors {
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
                ... on ProjectNotFoundError {
                    correlationId
                    message
                }
            }
            group {
                ...GroupFields
            }
        }
    }
    ${GROUP_FIELDS}
`

export const DELETE_ACCESS_GROUP = gql`
    mutation deleteAccessGroup($groupId: ID!) {
        deleteAccessGroup(input: { groupId: $groupId }) {
            errors {
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on MutationTargetNotFoundError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
            }
        }
    }
`

export const UPDATE_ACCESS_GROUP_NAME = gql`
    mutation setAccessGroupName($groupId: ID!, $name: String!) {
        setAccessGroupName(input: { groupId: $groupId, name: $name }) {
            group {
                ...GroupFields
            }
            errors {
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on MutationTargetNotFoundError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
            }
        }
    }
    ${GROUP_FIELDS}
`

export const UPDATE_ACCESS_GROUP_DISABLE_ANTI_PASSBACK = gql`
    mutation setAccessGroupDisableAntiPassback($value: Boolean!, $groupId: ID!) {
        setAccessGroupDisableAntiPassback(
            input: { disableAntiPassback: $value, groupId: $groupId }
        ) {
            errors {
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on MutationTargetNotFoundError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
            }
            group {
                ...GroupFields
            }
        }
    }
    ${GROUP_FIELDS}
`

export const UPDATE_ACCESS_GROUP_GRANT_WHEN_PSI_COMPLETED = gql`
    mutation setAccessGroupGrantWhenPsiCompleted($value: Boolean!, $groupId: ID!) {
        setAccessGroupGrantWhenPsiCompleted(
            input: { grantWhenPsiCompleted: $value, groupId: $groupId }
        ) {
            errors {
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on MutationTargetNotFoundError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
            }
            group {
                ...GroupFields
            }
        }
    }
    ${GROUP_FIELDS}
`
export const UPDATE_ACCESS_GROUP_IGNORE_GEOFENCE_WHEN_OPENING_WITH_APP = gql`
    mutation setAccessGroupIgnoreGeofenceWhenOpeningWithApp($value: Boolean!, $groupId: ID!) {
        setAccessGroupIgnoreGeofenceWhenOpeningWithApp(
            input: { ignoreGeofenceWhenOpeningWithApp: $value, groupId: $groupId }
        ) {
            errors {
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on MutationTargetNotFoundError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
            }
            group {
                ...GroupFields
            }
        }
    }
    ${GROUP_FIELDS}
`

export const CREATE_ACCESS_GROUP_READER_RULE_ASSOCIATION = gql`
    mutation createAccessGroupReaderRuleAssociation($groupId: ID!, $readerId: ID!, $ruleId: ID!) {
        createAccessGroupReaderRuleAssociation(
            input: { groupId: $groupId, readerId: $readerId, ruleId: $ruleId }
        ) {
            association {
                ...GroupReaderRuleFields
            }
            errors {
                ... on AccessGroupNotFoundError {
                    correlationId
                    message
                }
                ... on AccessRuleNotFoundError {
                    correlationId
                    message
                }
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on GroupReaderAndRuleMustBeFromSameProjectError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
                ... on ProjectReaderNotFoundError {
                    correlationId
                    message
                }
                ... on AccessGroupReaderRuleAssociationAlreadyExistsError {
                    correlationId
                    message
                }
            }
        }
    }
    ${GROUP_READER_RULE_FIELDS}
`

export const DELETE_ACCESS_GROUP_READER_RULE_ASSOCIATION = gql`
    mutation deleteAccessGroupReaderRuleAssociation($groupId: ID!, $readerId: ID!, $ruleId: ID!) {
        deleteAccessGroupReaderRuleAssociation(
            input: { groupId: $groupId, readerId: $readerId, ruleId: $ruleId }
        ) {
            errors {
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on MutationTargetNotFoundError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
            }
        }
    }
`

export const SET_ACCESS_GROUP_READER_RULE_ASSOCIATION_REQUIRES_PIN = gql`
    mutation setAccessGroupReaderRuleAssociationRequiresPin(
        $groupId: ID!
        $readerId: ID!
        $ruleId: ID!
        $value: Boolean!
    ) {
        setAccessGroupReaderRuleAssociationRequiresPin(
            input: { groupId: $groupId, readerId: $readerId, ruleId: $ruleId, requiresPin: $value }
        ) {
            errors {
                ... on NotAllowedError {
                    correlationId
                    message
                }
                ... on MutationTargetNotFoundError {
                    correlationId
                    message
                }
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
            }
        }
    }
`

export const SET_ACCESS_GROUP_READER_RULE_ASSOCIATION_CAN_BE_OPENED_WITH_APP = gql`
    mutation setAccessGroupReaderRuleAssociationCanBeOpenedWithApp(
        $groupId: ID!
        $readerId: ID!
        $ruleId: ID!
        $value: Boolean!
    ) {
        setAccessGroupReaderRuleAssociationCanBeOpenedWithApp(
            input: {
                groupId: $groupId
                readerId: $readerId
                ruleId: $ruleId
                canBeOpenedWithApp: $value
            }
        ) {
            errors {
                ... on ConcurrencyLockTimeoutError {
                    correlationId
                    message
                }
                ... on MutationTargetNotFoundError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
            }
        }
    }
`

export const CREATE_VIRTUAL_CARD = gql`
    mutation createVirtualCard(
        $companyId: ID!
        $firstName: String!
        $lastName: String!
        $mobile: MobileNumber!
        $projectId: ID!
        $withPsi: Boolean!
    ) {
        createVirtualCard(
            input: {
                companyId: $companyId
                firstName: $firstName
                lastName: $lastName
                mobile: $mobile
                projectId: $projectId
            }
        ) {
            query {
                allAccessGroupCardAssociationsForProject(projectId: $projectId) {
                    ...GroupCardFields
                }
            }
            errors {
                ... on CardAlreadyExistsError {
                    correlationId
                    message
                }
                ... on CompanyNotFoundError {
                    correlationId
                    message
                }
                ... on NotAllowedError {
                    correlationId
                    message
                }
                ... on ProjectNotFoundError {
                    correlationId
                    message
                }
            }
        }
    }
    ${GROUP_CARD_FIELDS}
`
